<script lang="ts">
    import { createDialog, melt } from '@melt-ui/svelte';
    import { setContext } from 'svelte';
    import { fade } from 'svelte/transition';

    import { trans } from '$assets/shop-translator.js';
    import { flyDrawer } from '$saga/utils/customTransitions';
    import PillarFiltersDrawer from '$saga/views/Search/components/drawer/PillarFiltersDrawer/PillarFiltersDrawer.svelte';
    import SecondaryFiltersDrawer from '$saga/views/Search/components/drawer/SecondaryFiltersDrawer.svelte';
    import SearchFormFields from '$saga/views/Search/components/SearchFormFields.svelte';
    import VerticalFiltersBar from '$saga/views/Search/components/verticalFilters/VerticalFiltersBar.svelte';
    import MobileFiltersBar from '$saga/views/Search/MobileFiltersBar.svelte';
    import { FilterType } from '$saga/views/Search/types';
    import {
        debouncedHandleFilterChange,
        FILTER_CHANGE_CONTEXT,
    } from '$saga/views/Search/utils';

    export let openAccordions: boolean;

    setContext(FILTER_CHANGE_CONTEXT, debouncedHandleFilterChange);

    let selectedFilterType: FilterType = FilterType.SELECTION;
    // Desktop sidebar pillars open the drawer on that pillar only, with no list to go back to
    let isPillarOnly = false;

    const {
        elements: { title, overlay, content, close, portalled },
        states: { open },
    } = createDialog({
        forceVisible: true,
    });

    $: pillarTitle = (() => {
        switch (selectedFilterType) {
            case FilterType.DESTINATION:
                return trans('search.bar.search_destination');
            case FilterType.SPORT:
                return trans('search.bar.search_sport');
            case FilterType.PAX:
                return trans('search.bar.travelers');
            case FilterType.STARTDATE:
                return trans('search.bar.departure_date');
            default:
                return trans('search.bar.search');
        }
    })();

    const openList = () => {
        selectedFilterType = FilterType.SELECTION;
        isPillarOnly = false;
        open.set(true);
    };

    const openPillar = (filterType: FilterType) => {
        if (!$open) {
            isPillarOnly = true;
        }
        selectedFilterType = filterType;
        open.set(true);
    };

    const resetView = () => {
        selectedFilterType = FilterType.SELECTION;
        if (isPillarOnly) {
            open.set(false);
        }
    };

    const closeDrawer = () => {
        open.set(false);
    };
</script>

<SearchFormFields />

<MobileFiltersBar onOpenFilters={openList} />

<VerticalFiltersBar {openAccordions} handleSelectedFilter={openPillar} />

{#if $open}
    <div use:melt={$portalled}>
        <div
            use:melt={$overlay}
            class="fixed inset-0 z-40 bg-black/50"
            transition:fade={{ duration: 250 }}
        ></div>
        <div
            use:melt={$content}
            class="fixed inset-x-0 bottom-0 z-50 flex h-full max-h-[calc(100%)] w-full flex-col bg-white transition-all duration-500 desktop:inset-x-[unset] desktop:right-0 desktop:top-0 desktop:min-w-[33vw] desktop:max-w-[33vw]"
            transition:flyDrawer
        >
            {#if selectedFilterType === FilterType.SELECTION}
                <SecondaryFiltersDrawer
                    {title}
                    {close}
                    {resetView}
                    {closeDrawer}
                    handleSelectedFilter={openPillar}
                />
            {:else}
                <PillarFiltersDrawer
                    {selectedFilterType}
                    handleSelectedFilter={openPillar}
                    pillarFiltersMenuTitle={pillarTitle}
                    {title}
                    {close}
                    {resetView}
                />
            {/if}
        </div>
    </div>
{/if}
